import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { useSession } from '../../lib/session.js'
import { useTRPC } from '../../lib/trpc.js'
import { EcoFace, EcoLauncher } from './EcoButtons.js'
import { EcoPanel } from './EcoPanel.js'
import { toggleEco, useEcoState } from './store.js'

/**
 * Eco in the shell: the panel when it is open, and ⌘J / Ctrl+J from anywhere.
 * The buttons that open it live where each shell has room — the rail on a
 * desk, a corner button in the field app. Only for people who may use the
 * assistant (`ASSISTANT_USE`, Owner and Office). Decision 116.
 */
export function Eco() {
  const { can } = useSession()
  const { open } = useEcoState()
  const allowed = can('ASSISTANT_USE')
  // On Eco's own page the side panel stands aside; ⌘J leaves the page instead.
  const onPage = useLocation().pathname === '/eco'

  useEffect(() => {
    if (!allowed) return
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'j') {
        event.preventDefault()
        if (window.location.pathname === '/eco') window.history.back()
        else toggleEco()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [allowed])

  return allowed && open && !onPage ? <EcoPanel /> : null
}

/** The rail's Eco: the live head, above Help. */
export function EcoRailButton() {
  const { can } = useSession()
  const { open } = useEcoState()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const onPage = useLocation().pathname === '/eco'
  const allowed = can('ASSISTANT_USE')
  // A card waiting for this person anywhere in Eco: the green square, as Help
  // and the Inbox show theirs. Read on focus and once a minute.
  const list = useQuery({ ...trpc.assistant.ecoSessions.queryOptions(), enabled: allowed, refetchInterval: 60_000 })
  const waiting = list.data?.waiting ?? 0
  if (!allowed) return null
  const lit = open || onPage
  return (
    <button
      type="button"
      onClick={() => (onPage ? navigate('/') : toggleEco())}
      aria-expanded={open}
      aria-label={waiting ? `Eco, the assistant — ${waiting} waiting for you` : 'Eco, the assistant'}
      aria-keyshortcuts="Meta+J Control+J"
      title="Ask Eco (⌘J)"
      className={cn(
        'relative mb-1 flex w-14 flex-col items-center gap-0.5 rounded-md py-1 transition-colors',
        lit ? 'bg-white/[0.09] text-white shadow-[inset_3px_0_0_var(--color-brand-500)]' : 'text-ink-400 hover:bg-white/[0.06] hover:text-white',
      )}
    >
      {waiting && !lit ? <span className="absolute top-1 right-2.5 size-2 rounded-[2px] bg-brand-500 ring-2 ring-ink-900" aria-hidden="true" /> : null}
      <EcoFace className="ecb-face-sm" />
      <span className="text-[9px] font-semibold">Eco</span>
    </button>
  )
}

/** The field app's Eco: a corner button above the tabs. */
export function EcoCornerButton({ className }: { className?: string }) {
  const { can } = useSession()
  const { open } = useEcoState()
  // On Eco's own page it would sit on the message box.
  const onPage = useLocation().pathname === '/eco'
  if (!can('ASSISTANT_USE') || open || onPage) return null
  return (
    <EcoLauncher onClick={toggleEco} className={cn('fixed right-3 bottom-[calc(4.75rem+var(--safe-bottom,0px))] z-30', className)}>
      Ask Eco
    </EcoLauncher>
  )
}
